import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Loader2, MessageCircle, UserPlus, Activity as ActivityIcon } from "lucide-react";
import axios from "axios";
import { AppHeader } from "@/components/AppHeader";
import { Avatar } from "@/components/Avatar";
import { useFamily } from "@/lib/useFamily";
import { formatDate, timeAgo } from "@/lib/dates";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_FILTERS = [
    { id: "", label: "All" },
    { id: "comment", label: "Comments" },
    { id: "heart", label: "Hearts" },
    { id: "member_joined", label: "New members" },
];

const KIND_ICON = { comment: MessageCircle, heart: Heart, member_joined: UserPlus };

function activityText(a) {
    if (a.kind === "member_joined") return "joined the family";
    return a.kind === "heart" ? "loved" : "commented on";
}

function dayLabel(iso) {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    const sameDay = (a, b) => a.toDateString() === b.toDateString();
    if (sameDay(d, today)) return "Today";
    if (sameDay(d, yesterday)) return "Yesterday";
    return formatDate(iso, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
}

export default function Activity() {
    const { checking, user, family } = useFamily();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actorId, setActorId] = useState("");
    const [kind, setKind] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (actorId) params.set("actor_id", actorId);
            if (kind) params.set("kind", kind);
            params.set("limit", "100");
            const { data } = await axios.get(`${API}/families/activity?${params.toString()}`, { withCredentials: true });
            setItems(data.activity);
        } catch {}
        setLoading(false);
    }, [actorId, kind]);

    useEffect(() => {
        if (!checking) load();
    }, [checking, load]);

    if (checking) {
        return (
            <main data-testid="activity-loading" className="flex min-h-screen items-center justify-center bg-cream">
                <Loader2 size={32} className="animate-spin text-lavender" />
            </main>
        );
    }

    const groups = [];
    items.forEach((a) => {
        const label = dayLabel(a.created_at);
        const last = groups[groups.length - 1];
        if (last && last.label === label) last.items.push(a);
        else groups.push({ label, items: [a] });
    });

    return (
        <main data-testid="activity-page" className="min-h-screen bg-cream text-navy">
            <AppHeader user={user} />
            <div className="mx-auto max-w-3xl px-6 py-10">
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.76, 0, 0.24, 1] }}>
                    <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.3em] text-[#C98A94]">{family.name}</p>
                    <h1 data-testid="activity-heading" className="mt-2 font-display text-3xl tracking-tight md:text-4xl">Family Activity</h1>

                    <div className="mt-6 flex flex-wrap items-center gap-3">
                        <div data-testid="activity-kind-filters" className="flex flex-wrap gap-2">
                            {KIND_FILTERS.map((k) => (
                                <button
                                    key={k.id}
                                    data-testid={`filter-kind-${k.id || "all"}`}
                                    onClick={() => setKind(k.id)}
                                    className={`rounded-full border-2 px-4 py-2 text-xs font-semibold transition-colors duration-300 ${
                                        kind === k.id ? "border-navy bg-navy text-white" : "border-warmgray text-navy hover:border-peach"
                                    }`}
                                >
                                    {k.label}
                                </button>
                            ))}
                        </div>
                        <select
                            data-testid="activity-person-filter"
                            value={actorId}
                            onChange={(e) => setActorId(e.target.value)}
                            aria-label="Filter by person"
                            className="rounded-full border-2 border-warmgray bg-ivory px-4 py-2 text-xs font-semibold text-navy outline-none transition-colors duration-300 focus:border-peach"
                        >
                            <option value="">Everyone</option>
                            {family.members.map((m) => (
                                <option key={m.user_id} value={m.user_id}>
                                    {m.name || m.email}
                                </option>
                            ))}
                        </select>
                    </div>
                </motion.div>

                {loading ? (
                    <div className="mt-16 flex justify-center">
                        <Loader2 size={28} className="animate-spin text-lavender" />
                    </div>
                ) : items.length === 0 ? (
                    <div data-testid="activity-empty" className="mt-10 rounded-3xl border-2 border-dashed border-warmgray bg-ivory p-14 text-center">
                        <ActivityIcon size={32} className="mx-auto text-lavender" />
                        <h2 className="mt-4 font-display text-2xl">Nothing here yet.</h2>
                        <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-navy">
                            {actorId || kind
                                ? "No activity matches these filters — try widening them."
                                : "Comments, hearts, and new family members will appear here as your archive comes alive."}
                        </p>
                    </div>
                ) : (
                    <div data-testid="activity-list" className="mt-8">
                        {groups.map((g) => (
                            <div key={g.label} className="mb-8">
                                <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-[#C98A94]">{g.label}</p>
                                <div className="mt-3 space-y-3">
                                    {g.items.map((a, i) => {
                                        const Icon = KIND_ICON[a.kind] || MessageCircle;
                                        const row = (
                                            <div className="flex items-center gap-3.5 rounded-2xl border-2 border-warmgray bg-ivory px-5 py-4 transition-colors duration-300 hover:border-peach">
                                                <Avatar person={{ name: a.actor_name, picture: a.actor_picture, avatar_path: a.actor_avatar_path }} size={40} />
                                                <div className="min-w-0 flex-1">
                                                    <p className="text-sm text-navy">
                                                        <span className="font-semibold">{a.actor_name}</span> {activityText(a)}{" "}
                                                        {a.memory_label && <span className="font-medium italic">“{a.memory_label}”</span>}
                                                    </p>
                                                    <p className="mt-0.5 font-mono text-[10px] uppercase tracking-widest text-navy/50">
                                                        {timeAgo(a.created_at)} · {formatDate(a.created_at)}
                                                    </p>
                                                </div>
                                                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-lavender to-peach">
                                                    <Icon size={13} className="text-white" />
                                                </span>
                                            </div>
                                        );
                                        return a.memory_id ? (
                                            <Link key={i} to={`/memories/${a.memory_id}`} data-testid={`activity-row-${i}`} className="block">
                                                {row}
                                            </Link>
                                        ) : (
                                            <div key={i} data-testid={`activity-row-${i}`}>
                                                {row}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </main>
    );
}
